import { getAllQuestionnaireAnswers } from "@medplum/core";
import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import EncounterServiceDateDiffFields from "@/encounters/components/EncounterServiceDateDiffFields/EncounterServiceDateDiffFields";
import { EncounterAnswersLinkIds } from "@/encounters/constants/questionnaire-questions";
import {
  EncounterInfoDiffFields,
  getEncounterInfoFieldsFromEncounters,
} from "@/encounters/utils/encounter-detail-version-diff";
import { getEncounterServiceDateDiffContext } from "@/encounters/utils/encounter-service-date-diff";

/**
 * Reads the Encounter Information fields each version's own answers recorded.
 *
 * A field a version did not record stays undefined, so its column shows no value rather than a
 * value borrowed from the live Encounter, which may differ from what that version held.
 *
 * @param currentVersion The amendment being viewed.
 * @param previousVersion The version the amendment is compared against.
 * @returns The per-version template, type, location, and service-date diff.
 */
function getFieldsFromAnswers(
  currentVersion: QuestionnaireResponse,
  previousVersion: QuestionnaireResponse,
): EncounterInfoDiffFields {
  const currentAnswers = getAllQuestionnaireAnswers(currentVersion);
  const previousAnswers = getAllQuestionnaireAnswers(previousVersion);

  return {
    currentTemplate:
      currentAnswers[EncounterAnswersLinkIds.EncounterTemplate]?.[0]?.valueCoding?.display,
    previousTemplate:
      previousAnswers[EncounterAnswersLinkIds.EncounterTemplate]?.[0]?.valueCoding?.display,
    currentType: currentAnswers[EncounterAnswersLinkIds.EncounterType]?.[0]?.valueCoding?.display,
    previousType: previousAnswers[EncounterAnswersLinkIds.EncounterType]?.[0]?.valueCoding?.display,
    currentLocation:
      currentAnswers[EncounterAnswersLinkIds.EncounterLocation]?.[0]?.valueCoding?.display,
    previousLocation:
      previousAnswers[EncounterAnswersLinkIds.EncounterLocation]?.[0]?.valueCoding?.display,
    serviceDateDiff: getEncounterServiceDateDiffContext({ currentAnswers, previousAnswers }),
  };
}

function getFields({
  currentVersion,
  previousVersion,
  currentEncounter,
  previousEncounter,
}: {
  currentVersion?: QuestionnaireResponse;
  previousVersion?: QuestionnaireResponse;
  currentEncounter?: Encounter;
  previousEncounter?: Encounter;
}): EncounterInfoDiffFields | undefined {
  if (currentEncounter && previousEncounter) {
    return getEncounterInfoFieldsFromEncounters(currentEncounter, previousEncounter);
  }

  if (currentVersion && previousVersion) {
    return getFieldsFromAnswers(currentVersion, previousVersion);
  }

  return undefined;
}

export const EncounterDifference = ({
  currentVersion,
  previousVersion,
  currentEncounter,
  previousEncounter,
}: {
  currentVersion?: QuestionnaireResponse;
  previousVersion?: QuestionnaireResponse;
  currentEncounter?: Encounter;
  previousEncounter?: Encounter;
}) => {
  const fields = getFields({
    currentVersion,
    previousVersion,
    currentEncounter,
    previousEncounter,
  });

  if (!fields) return null;

  const {
    currentTemplate,
    previousTemplate,
    currentType,
    previousType,
    currentLocation,
    previousLocation,
    serviceDateDiff,
  } = fields;

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Encounter Information</Title>
      </Stack>
      <Group grow align="flex-start">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="0">
              <TextWrapper fw={600}>Template</TextWrapper>
              <WordsDiff showRemoved current={currentTemplate} previous={previousTemplate} />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>Type</TextWrapper>
              <WordsDiff showRemoved current={currentType} previous={previousType} />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>Location</TextWrapper>
              <WordsDiff showRemoved current={currentLocation} previous={previousLocation} />
            </Stack>
            {serviceDateDiff && (
              <EncounterServiceDateDiffFields variant="previous" diff={serviceDateDiff} />
            )}
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="0">
              <TextWrapper fw={600}>Template</TextWrapper>
              <WordsDiff current={currentTemplate} previous={previousTemplate} />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>Type</TextWrapper>
              <WordsDiff current={currentType} previous={previousType} />
            </Stack>

            <Stack gap="0">
              <TextWrapper fw={600}>Location</TextWrapper>
              <WordsDiff current={currentLocation} previous={previousLocation} />
            </Stack>
            {serviceDateDiff && (
              <EncounterServiceDateDiffFields variant="amended" diff={serviceDateDiff} />
            )}
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};
